import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import useMasterData from '../../hooks/useMasterData';
import FormInput from '../../components/FormInput';
import FormSelect from '../../components/FormSelect';
import FormFileInput from '../../components/FormFileInput';
import { supabase } from '../../services/supabaseClient';
import DokumenSusulan from '../../components/DokumenSusulan';

const MemberStatus = () => {
  const { memberStatus, session } = useAuth();
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [existingData, setExistingData] = useState(null);

  const { register, handleSubmit, watch, reset, formState: { errors, isSubmitting } } = useForm({
    mode: 'onChange'
  });

  const {
    provinces,
    businessTypes,
    cities,
    isLoading: isMasterLoading,
    fetchCities
  } = useMasterData();

  const selectedProvince = watch('provinsi');

  useEffect(() => {
    if (selectedProvince) {
      fetchCities(selectedProvince);
    }
  }, [selectedProvince, fetchCities]);

  useEffect(() => {
    let isMounted = true;

    if (memberStatus === 'Perlu perbaikan' && session?.user?.id) {
      const fetchUserData = async () => {
        setIsLoadingData(true);
        try {
          const { data, error } = await supabase
            .from('registrations')
            .select('*')
            .eq('user_id', session.user.id)
            .single();

          if (error) throw error;

          if (isMounted) {
            setExistingData(data);
            reset({
              namaPesantren: data.pesantren_name,
              pimpinan: data.leader_name,
              penanggungJawab: data.pic_hebitren_name,
              nomorKontak: data.contact_number,
              provinsi: data.province_id,
              kota: data.city_id,
              jenisUsaha: data.business_type_id,
            });

            if (data.province_id) {
              fetchCities(data.province_id);
            }
          }
        } catch (error) {
          console.error('Error fetching existing data:', error);
          alert('Terjadi kesalahan saat memuat data Anda sebelumnya.');
        } finally {
          if (isMounted) setIsLoadingData(false);
        }
      };
      fetchUserData();
    }

    return () => { isMounted = false; };
  }, [memberStatus, session, reset, fetchCities]);

  const onSubmitUpdate = async (data) => {
    try {
      const userId = session?.user?.id;
      if (!userId) throw new Error('Sesi pengguna tidak valid.');

      let logoUrl = existingData.logo_url;
      let documentUrl = existingData.document_url;

      if (data.logo && data.logo.length > 0) {
        const logoFile = data.logo[0];
        const logoFileName = `${userId}_${Date.now()}_${logoFile.name}`;
        const { error: logoError } = await supabase.storage
          .from('registration_logos')
          .upload(logoFileName, logoFile);

        if (logoError) throw new Error('Gagal mengunggah logo: ' + logoError.message);

        const { data: logoUrlData } = supabase.storage
          .from('registration_logos')
          .getPublicUrl(logoFileName);
        logoUrl = logoUrlData.publicUrl;
      }

      if (data.legalitas && data.legalitas.length > 0) {
        const legalitasFile = data.legalitas[0];
        const legalitasFileName = `${userId}_${Date.now()}_${legalitasFile.name}`;
        const { error: legalitasError } = await supabase.storage
          .from('registration_docs')
          .upload(legalitasFileName, legalitasFile);

        if (legalitasError) throw new Error('Gagal mengunggah dokumen legalitas: ' + legalitasError.message);

        const { data: legalitasUrlData } = supabase.storage
          .from('registration_docs')
          .getPublicUrl(legalitasFileName);
        documentUrl = legalitasUrlData.publicUrl;
      }

      const { error: updateError } = await supabase
        .from('registrations')
        .update({
          pesantren_name: data.namaPesantren,
          leader_name: data.pimpinan,
          pic_hebitren_name: data.penanggungJawab,
          contact_number: data.nomorKontak,
          province_id: data.provinsi,
          city_id: data.kota,
          business_type_id: data.jenisUsaha,
          logo_url: logoUrl,
          document_url: documentUrl,
          status: 'Menunggu verifikasi'
        })
        .eq('user_id', userId);

      if (updateError) {
        throw new Error('Gagal memperbarui data pendaftaran: ' + updateError.message);
      }

      alert('Data pendaftaran berhasil diperbarui! Halaman akan dimuat ulang.');
      window.location.reload();

    } catch (error) {
      console.error('Update Submission Error:', error);
      alert('Proses pembaruan gagal. Silakan coba lagi.\nDetail: ' + error.message);
    }
  };

  // Fungsi pemutus sesi untuk mengeluarkan user dari Dead-End
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error saat logout:', error);
    }
  };

  if (memberStatus === 'Menunggu verifikasi' || memberStatus === 'Menunggu verifikasi nasional') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white max-w-md w-full p-8 rounded-lg shadow-md text-center border border-gray-200">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 mb-6">
            <svg className="h-8 w-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Menunggu Verifikasi</h2>
          <p className="text-gray-600 mb-6">
            Berkas pendaftaran Anda sedang dalam antrean pemeriksaan oleh tim verifikator. Silakan cek status ini secara berkala.
          </p>
          <DokumenSusulan />
          <button
            onClick={handleLogout}
            className="text-sm font-medium text-gray-500 hover:text-red-600 transition-colors"
          >
            Keluar dari akun
          </button>
        </div>
      </div>
    );
  }

  if (memberStatus === 'Ditolak') {
    return (
      <div className="min-h-screen bg-red-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white max-w-md w-full p-8 rounded-lg shadow-md text-center border-t-4 border-red-600">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
            <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Pendaftaran Ditolak</h2>
          <p className="text-gray-600 mb-6">
            Mohon maaf, pendaftaran Anda tidak dapat dilanjutkan karena tidak memenuhi syarat keanggotaan HEBITREN.
          </p>
          <button
            onClick={handleLogout}
            className="text-sm font-medium text-gray-500 hover:text-red-600 transition-colors"
          >
            Keluar dari akun
          </button>
        </div>
      </div>
    );
  }

  if (memberStatus === 'Disetujui') {
    return <Outlet />;
  }

  if (memberStatus === 'Perlu perbaikan') {
    if (isLoadingData) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <p className="text-gray-500 font-medium animate-pulse">Memuat data pendaftaran Anda...</p>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8 flex flex-col justify-center items-center">
        <div className="w-full max-w-3xl flex justify-end mb-4">
          <button
            onClick={handleLogout}
            className="text-sm font-medium text-gray-500 hover:text-red-600 transition-colors flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Keluar
          </button>
        </div>
        <div className="max-w-3xl w-full bg-white p-8 shadow-md rounded-lg border border-gray-200">
          <div className="mb-8 border-b pb-4">
            <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-md p-4 mb-6 flex items-start">
              <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="font-bold">Perlu Perbaikan Data</h3>
                <p className="text-sm mt-1">
                  Tim verifikator meminta Anda memperbaiki beberapa data pendaftaran. Silakan perbarui form di bawah ini dan tekan simpan. (Biarkan form unggah kosong jika tidak ingin mengubah dokumen lama).
                </p>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Formulir Perbaikan Pendaftaran</h2>
          </div>

          <form onSubmit={handleSubmit(onSubmitUpdate)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput label="Identitas (Nama Pesantren)" name="namaPesantren" register={register} errors={errors} rules={{ required: 'Nama Pesantren wajib diisi' }} />
              <FormInput label="Pimpinan Pesantren" name="pimpinan" register={register} errors={errors} rules={{ required: 'Nama pimpinan wajib diisi' }} />
              <FormInput label="Penanggung Jawab Pesantren" name="penanggungJawab" register={register} errors={errors} rules={{ required: 'Penanggung jawab wajib diisi' }} />
              <FormInput label="Nomor Kontak" name="nomorKontak" type="tel" register={register} errors={errors} rules={{ required: 'Nomor kontak wajib diisi', pattern: { value: /^[0-9]+$/, message: 'Format harus berupa angka' } }} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4 border-gray-100">
              <FormSelect label="Provinsi" name="provinsi" options={provinces} register={register} errors={errors} disabled={isMasterLoading || isSubmitting} placeholder={isMasterLoading ? "Memuat data..." : "Pilih Provinsi..."} rules={{ required: 'Provinsi wajib dipilih' }} />
              <FormSelect label="Kota / Kabupaten" name="kota" options={cities} register={register} errors={errors} disabled={!selectedProvince || isMasterLoading || isSubmitting} placeholder={!selectedProvince ? "Pilih provinsi terlebih dahulu" : "Pilih Kota..."} rules={{ required: 'Kota wajib dipilih' }} />
            </div>

            <div className="border-t pt-4 border-gray-100">
              <FormSelect label="Jenis Usaha Dasar" name="jenisUsaha" options={businessTypes} register={register} errors={errors} disabled={isMasterLoading || isSubmitting} placeholder={isMasterLoading ? "Memuat data..." : "Pilih Jenis Usaha..."} rules={{ required: 'Jenis usaha dasar wajib dipilih' }} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4 border-gray-100">
              <FormFileInput label="Upload Logo Baru (Opsional)" name="logo" accept=".jpeg,.jpg,.png,.webp" hint="Biarkan kosong jika tidak ingin mengubah logo. Format: JPEG, PNG, WEBP. Maks: 2MB." register={register} errors={errors} rules={{ validate: { acceptedFormats: (files) => { if (!files || files.length === 0) return true; const type = files[0].type; return ['image/jpeg', 'image/png', 'image/webp'].includes(type) || 'Format harus JPEG, PNG, atau WEBP'; }, maxSize: (files) => { if (!files || files.length === 0) return true; return files[0].size <= 2 * 1024 * 1024 || 'Ukuran maksimal 2MB'; } } }} />
              <FormFileInput label="Upload Dokumen Legalitas Baru (Opsional)" name="legalitas" accept=".pdf" hint="Biarkan kosong jika tidak ingin mengubah dokumen. Format: PDF. Maks: 5MB." register={register} errors={errors} rules={{ validate: { acceptedFormats: (files) => { if (!files || files.length === 0) return true; return files[0].type === 'application/pdf' || 'Format harus PDF'; }, maxSize: (files) => { if (!files || files.length === 0) return true; return files[0].size <= 5 * 1024 * 1024 || 'Ukuran maksimal 5MB'; } } }} />
            </div>

            <div className="pt-4 flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`bg-blue-600 text-white font-medium py-2 px-6 rounded-md shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-700'}`}
              >
                {isSubmitting ? 'Menyimpan...' : 'Simpan & Kirim Ulang'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <p className="text-gray-500 font-medium">Memeriksa status akun...</p>
    </div>
  );
};

export default MemberStatus;